import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import CloudIcon from '@material-ui/icons/Cloud';
import DnsIcon from '@material-ui/icons/Dns';
import EmailIcon from '@material-ui/icons/Email';
import WebIcon from '@material-ui/icons/Web';
import PublishIcon from '@material-ui/icons/Publish';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import {
  networkFoundationPermission,
  dnsRecordPermission,
  gmailDelegationPermission,
  staticSitePermission,
  staticSitePromotePermission,
} from '../../plugins/conduitPermissions';

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Nav visibility follows the same permissions that gate each page, so the
      // role -> permission mapping in the backend policy stays the only source
      // of truth. Hiding is UX only; the page gate and backend still enforce.
      const canNetworkFoundation = usePermission({
        permission: networkFoundationPermission,
      }).allowed;
      const canDnsRecord = usePermission({
        permission: dnsRecordPermission,
      }).allowed;
      const canGam = usePermission({
        permission: gmailDelegationPermission,
      }).allowed;
      const canStaticSite = usePermission({
        permission: staticSitePermission,
      }).allowed;
      const canStaticSitePromote = usePermission({
        permission: staticSitePromotePermission,
      }).allowed;
      const canAnyFunction =
        canNetworkFoundation ||
        canDnsRecord ||
        canGam ||
        canStaticSite ||
        canStaticSitePromote;

      nav.take('page:search');
      nav.take('page:notifications');
      nav.take('page:conduit-network-foundation');
      nav.take('page:conduit-dns-record');
      nav.take('page:conduit-static-site');
      nav.take('page:conduit-static-site-promote');
      nav.take('page:conduit-gmail-delegation/hub');
      nav.take('page:conduit-gmail-delegation/manage');
      nav.take('page:conduit-gmail-delegation/view');
      nav.take('page:conduit-gmail-delegation/aliases');
      nav.take('page:conduit-gmail-delegation/calendar');
      nav.take('page:conduit-gmail-delegation/user-profile');

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {nav.take('page:catalog')}
            {canAnyFunction && <SidebarDivider />}
            {canNetworkFoundation && (
              <SidebarItem
                icon={CloudIcon}
                to="/network-foundation"
                text="Network Foundation"
              />
            )}
            {canDnsRecord && (
              <SidebarItem
                icon={DnsIcon}
                to="/dns-record"
                text="DNS Record"
              />
            )}
            {canStaticSite && (
              <SidebarItem
                icon={WebIcon}
                to="/static-site"
                text="Static Site"
              />
            )}
            {canStaticSitePromote && (
              <SidebarItem
                icon={PublishIcon}
                to="/static-site-promote"
                text="Promote to Production"
              />
            )}
            {canGam && (
              <SidebarItem
                icon={EmailIcon}
                to="/gam"
                text="GAM"
              />
            )}
            <SidebarDivider />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {nav.take('page:app-visualizer')}
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
